import { calcularRemessaExteriorEstimativa, arredondarBrl, cambioDaLinha } from './remessaExteriorEstimativaPolicy';
import { FeeIntelligence } from './feeIntelligence';
import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { OrcamentoClientInheritance } from './orcamentoClientInheritance';
import { cambioBrlLinha } from './moedaFornecedorPolicy';
import { comNfPropria, ehContaDiretaDaNfDoGrupo, fatorBaseNf } from './impostoNfContaDiretaPolicy';
import { ehClienteLongPing } from './longPingPolicy';
import { totalBrutoLinha } from './valorBrlPolicy';
import { impostoNfAccessDaLinha } from './colunaVigentePolicy';

// FEE do orçamento INTERNACIONAL (decisão do Rafael, 25/09/2026: "a planilha é a referência").
// Nas planilhas internacionais (AbbVie, Sanofi, Chiesi) a base do FEE é o subtotal da linha
// com a taxa do fornecedor (% Serviço e % Impostos) e os encargos da remessa (imposto de
// remessa, taxa de envio e NF sobre esses custos). No nacional segue a base limpa (17/09).
// O valor fica anotado na linha e o feeIntelligence (rawBaseByMoment) usa quando existe.
export const CAMPO_BASE_FEE_INTERNACIONAL = 'feeBaseInternacional';

// Regra B (Rafael 28/09, substitui a base limpa de 17/09 para estes clientes): no NACIONAL da família
// Sanofi (Sanofi/Opella/Medley, FEE 2043) e da Chiesi (FEE 130), a base do FEE de cada linha =
// unit × qtd × diárias × (1 + % Serviço da etapa), como nas planilhas modelo. % Impostos, % Imposto NF
// (gross-up) e encargos de remessa ficam FORA. A linha recebe o marcador e o feeIntelligence
// (rawBaseByMoment) soma o serviço. Espelho de OrcamentoFeeRuleDisplay::CAMPO_BASE_COM_SERVICO (API).
export const CAMPO_BASE_FEE_COM_SERVICO = 'feeBaseComServico';

// AbbVie (Rafael 06/10): no NACIONAL a base do FEE de cada linha é o "Total c/ imposto" da planilha do
// cliente (modelo oficial e EV2026-2057): unit × qtd × diárias × (1 + % Serviço + % Impostos do item).
// % Imposto NF (gross-up) fica fora. Espelho de OrcamentoFeeRuleDisplay::CAMPO_BASE_COM_IMPOSTO_ITEM (API).
export const CAMPO_BASE_FEE_COM_IMPOSTO_ITEM = 'feeBaseComImpostoItem';

// Chiesi NACIONAL (D-0153, Rafael 03/11: "o Fee é calculado com o imposto. Ex: RSVP deu 1000 + 19,05% = 1190,50 =>
// aplica o FEE neste valor"), como as planilhas antigas: a base de cada linha que compõe FEE soma o Imposto NF Access
// dela (NF virtual do grupo ou % Imposto NF próprio; colunaVigentePolicy.impostoNfAccessDaLinha). Valor por etapa, na
// moeda da linha, junto com o marcador da regra B. Espelho de OrcamentoFeeRuleDisplay::CAMPO_BASE_NF_ACCESS (API).
export const CAMPO_BASE_FEE_NF_ACCESS = 'feeBaseNfAccess';
const ID_ABBVIE = '1761594621000';

const semAcento = (valor) => String(valor ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

// Medley (filiais Campinas/São Paulo): a API (OrcamentoClienteInheritance) herda Sanofi pelo id; a
// herança do front ainda não tem esses ids (a de São Paulo tem modelo 'master').
const MEDLEY_SANOFI = ['714943861777307814', '50228141783430569'];

// AbbVie (modelo 'abbvie'; id/nome quando o cliente vem sem o modelo) — espelho de
// OrcamentoFeeRuleDisplay::clienteBaseComImpostoItem.
export function clienteBaseFeeComImpostoItem(cliente) {
    if (!cliente) return false;
    if (typeof cliente !== 'object') return String(cliente) === ID_ABBVIE;
    if (String(cliente.id ?? '') === ID_ABBVIE) return true;
    if (OrcamentoClientInheritance.resolveRuleModel(cliente) === 'abbvie') return true;
    if (OrcamentoClientInheritance.resolveVisualModel(cliente) === 'allergan') return true; // D-0152
    return semAcento(cliente.nome).includes('ABBVIE');
}

// Long Ping (D-0119): base do FEE = total da linha (com % Serviço, % Impostos e % Imposto NF), como o
// "Total c/ imposto" da planilha. Espelho de OrcamentoFeeRuleDisplay::clienteBaseComTotalLinha.
export function clienteBaseFeeComTotalLinha(cliente) {
    return ehClienteLongPing(cliente);
}

// Chiesi (nome) — espelho de OrcamentoFeeRuleDisplay::clienteBaseComNfAccess (D-0153).
export function clienteBaseFeeComNfAccess(cliente) {
    return !!cliente && typeof cliente === 'object' && semAcento(cliente.nome).includes('CHIESI');
}

// Sanofi/Opella/Medley (regras Sanofi) ou Chiesi (nome) — espelho de OrcamentoFeeRuleDisplay::clienteBaseComServico.
export function clienteBaseFeeComServico(cliente) {
    if (!cliente || typeof cliente !== 'object') return false;
    if (MEDLEY_SANOFI.includes(String(cliente.id ?? ''))) return true;
    if (OrcamentoClientInheritance.usesSanofiBudgetRules(cliente)) return true;
    return semAcento(cliente.nome).includes('CHIESI');
}

const ETAPAS = { pre: 'pre_evento', neg: 'negociado' };

const numero = (valor) => {
    if (valor === null || valor === undefined || valor === '') return 0;
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
    const texto = String(valor).trim();
    const convertido = Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(convertido) ? convertido : 0;
};

export function ehOrcamentoInternacional(nacionalidade) {
    return String(nacionalidade ?? '').trim().toLowerCase() === 'internacional';
}

function linhaDeFee(linha = {}) {
    const produto = linha.produto ?? {};
    return FeeIntelligence.isFeeCalculatorItem(produto) || AgenciaOrcamentoPolicy.isFeeAgenciaGlobal(produto);
}

const ALIQUOTA_NF_ACCESS = 0.1905;

// Chiesi: linha própria da Access recebe a NF Access automática "por fora" do grupo
// (não fica no total da linha). Na planilha ela está no subtotal que compõe o FEE.
export function comNfAccessAutomatica(linha = {}, valor = 0) {
    // Coordenação Chiesi no exterior (04/10): o principal também leva a NF no subtotal do bloco.
    if ((!linha.impostoNfAccessAutomaticoChiesi && !linha.nfAccessPrincipalExteriorChiesi) || numero(linha.imposto) > 0) return numero(valor);
    // D-0112: NF do grupo na conta direta (planilha antiga) = × (1 + a); padrão, por fora.
    return ehContaDiretaDaNfDoGrupo(linha) ? numero(valor) * (1 + ALIQUOTA_NF_ACCESS) : numero(valor) / (1 - ALIQUOTA_NF_ACCESS);
}

// Base por etapa na moeda DA LINHA: total da linha + encargos da remessa ÷ câmbio da linha (legado:
// câmbio global = moeda do orçamento). A linha de moeda própria vai para a moeda do orçamento pelo
// fator anotado (feeIntelligence.rawBaseByMoment), como na API (anotarBaseInternacional).
export function calcularBaseFeeInternacional(linha = {}, contexto = {}, fornecedorAccess = null) {
    const base = {
        pre_evento: comNfAccessAutomatica(linha, linha.totalPreEvento),
        negociado: comNfAccessAutomatica(linha, linha.totalNegociado),
        fechamento: comNfAccessAutomatica(linha, linha.totalFechamento),
    };
    const remessa = calcularRemessaExteriorEstimativa(linha, contexto, fornecedorAccess);
    if (remessa.elegivel) {
        Object.entries(ETAPAS).forEach(([etapa, momento]) => {
            const cambio = numero(cambioDaLinha(linha, contexto, etapa));
            if (cambio > 0) base[momento] += numero(remessa[etapa]?.adicional_brl) / cambio;
        });
    }
    return base;
}

// D-0153: NF Access da linha por etapa sobre o total dela (totalPreEvento/…); só anota quando há NF.
export function anotarNfAccessNaBaseFee(grupo = {}, linha = {}, fornecedorAccess = null, totais = null) {
    const opcoes = { aplicaImposto: true, clienteEhChiesi: true, fornecedorAccess };
    const nf = {
        pre_evento: impostoNfAccessDaLinha(grupo, linha, totais ? totais.pre_evento : linha.totalPreEvento, opcoes),
        negociado: impostoNfAccessDaLinha(grupo, linha, totais ? totais.negociado : linha.totalNegociado, opcoes),
        fechamento: impostoNfAccessDaLinha(grupo, linha, totais ? totais.fechamento : linha.totalFechamento, opcoes),
    };
    if (nf.pre_evento || nf.negociado || nf.fechamento) linha[CAMPO_BASE_FEE_NF_ACCESS] = nf;
    else delete linha[CAMPO_BASE_FEE_NF_ACCESS];
    return linha;
}

// Anota (ou remove) a base internacional em todas as linhas que não são o próprio FEE; no nacional,
// com contexto.baseComServico (clienteBaseFeeComServico), o marcador da regra B; com
// contexto.baseComImpostoItem (AbbVie, clienteBaseFeeComImpostoItem), o da base com % Impostos; com
// contexto.baseComTotalLinha (Long Ping), o total da linha sem arredondar (OrcamentoFeeRuleDisplay::totalLinhaByMoment).
export function anotarBaseFeeInternacional(orcamentoitem = [], contexto = {}, fornecedorAccess = null) {
    const internacional = ehOrcamentoInternacional(contexto.nacionalidade);
    if (!internacional && contexto.baseComTotalLinha) {
        (orcamentoitem || []).forEach((grupo) => (grupo.subitem || []).forEach((linha) => {
            delete linha[CAMPO_BASE_FEE_COM_SERVICO];
            delete linha[CAMPO_BASE_FEE_COM_IMPOSTO_ITEM];
            delete linha[CAMPO_BASE_FEE_INTERNACIONAL];
            if (linhaDeFee(linha)) return;
            linha[CAMPO_BASE_FEE_INTERNACIONAL] = {
                pre_evento: totalBrutoLinha(linha, 'PreEvento'),
                negociado: totalBrutoLinha(linha, 'Negociado'),
                fechamento: totalBrutoLinha(linha, 'Fechamento'),
            };
        }));
        return orcamentoitem;
    }
    const comServico = !internacional && !!contexto.baseComServico;
    const comImposto = !internacional && !comServico && !!contexto.baseComImpostoItem;
    // D-0153: Chiesi nacional — + Imposto NF Access da linha (o total já está calculado quando a anotação roda).
    const comNfAccess = comServico && !!contexto.baseComNfAccess;
    (orcamentoitem || []).forEach((grupo) => (grupo.subitem || []).forEach((linha) => {
        delete linha[CAMPO_BASE_FEE_COM_SERVICO];
        delete linha[CAMPO_BASE_FEE_COM_IMPOSTO_ITEM];
        delete linha[CAMPO_BASE_FEE_NF_ACCESS];
        if (!internacional || linhaDeFee(linha)) {
            delete linha[CAMPO_BASE_FEE_INTERNACIONAL];
            if (comServico && !linhaDeFee(linha)) linha[CAMPO_BASE_FEE_COM_SERVICO] = true;
            if (comImposto && !linhaDeFee(linha)) linha[CAMPO_BASE_FEE_COM_IMPOSTO_ITEM] = true;
            if (comNfAccess && !linhaDeFee(linha)) anotarNfAccessNaBaseFee(grupo, linha, fornecedorAccess);
            return;
        }
        linha[CAMPO_BASE_FEE_INTERNACIONAL] = calcularBaseFeeInternacional(linha, contexto, fornecedorAccess);
    }));
    return orcamentoitem;
}

// R$ exato (contrato 02/10, regra 4): no orçamento estrangeiro com linha de moeda própria
// (valorBrlPolicy.orcamentoBrlExato) a base do FEE é a soma dos R$ das linhas. Cada linha (não FEE)
// recebe a base dela em R$ por etapa, já arredondada, pela MESMA definição de base de hoje:
// - internacional (feeBaseInternacional): R$ do total da linha (total × câmbio da linha, 2 casas)
//   + encargos da remessa em R$ (Pré/Negociado);
// - demais: base limpa (unit × qtd × diárias; × (1 + % Serviço) na regra B) × câmbio da linha, 2 casas.
// O feeIntelligence (rawBaseByMoment) usa base ÷ câmbio do orçamento. Fora do modo exato a anotação
// é removida (nenhum número muda). Muta as linhas, como anotarBaseFeeInternacional.
export const CAMPO_BASE_FEE_BRL_EXATO = 'baseFeeBrlExato';
const MOMENTO_ETAPA = { pre_evento: 'PreEvento', negociado: 'Negociado', fechamento: 'Fechamento' };
const ETAPA_REMESSA = { pre_evento: 'pre', negociado: 'neg' };

export function calcularBaseFeeBrlExato(linha = {}, contexto = {}, fornecedorAccess = null) {
    const cambioGlobal = numero(contexto.cambio) > 0 ? numero(contexto.cambio) : 1;
    const remessa = linha[CAMPO_BASE_FEE_INTERNACIONAL] ? calcularRemessaExteriorEstimativa(linha, contexto, fornecedorAccess, { exato: true }) : { elegivel: false };
    const base = { cambio: cambioGlobal };
    Object.entries(MOMENTO_ETAPA).forEach(([momento, etapa]) => {
        const cambioLinha = cambioBrlLinha(linha, etapa, cambioGlobal);
        if (linha[CAMPO_BASE_FEE_INTERNACIONAL]) {
            const encargos = remessa.elegivel && ETAPA_REMESSA[momento] ? numero(remessa[ETAPA_REMESSA[momento]]?.adicional_brl) : 0;
            base[momento] = comNfAccessAutomatica(linha, linha[`total${etapa}`]) * cambioLinha + encargos;
            return;
        }
        let limpa = numero(linha[`quantidade${etapa}`]) * numero(linha[`diaria${etapa}`]) * numero(linha[`valorUnitario${etapa}`]);
        if (linha[CAMPO_BASE_FEE_COM_SERVICO]) limpa *= 1 + (numero(linha[`servico${etapa}`]) / 100);
        else if (linha[CAMPO_BASE_FEE_COM_IMPOSTO_ITEM]) limpa *= 1 + (numero(linha[`servico${etapa}`]) / 100) + (numero(linha.impostoPorcentagemItem) / 100);
        // D-0153: Chiesi nacional — + Imposto NF Access da linha (moeda da linha).
        if (linha[CAMPO_BASE_FEE_NF_ACCESS]) limpa += numero(linha[CAMPO_BASE_FEE_NF_ACCESS][momento]);
        base[momento] = limpa * cambioLinha;
    });
    return base;
}

export function anotarBaseFeeBrlExato(orcamentoitem = [], { exato = false, ...contexto } = {}, fornecedorAccess = null) {
    (orcamentoitem || []).forEach((grupo) => (grupo.subitem || []).forEach((linha) => {
        delete linha[CAMPO_BASE_FEE_BRL_EXATO];
        if (!exato || linhaDeFee(linha)) return;
        linha[CAMPO_BASE_FEE_BRL_EXATO] = calcularBaseFeeBrlExato(linha, contexto, fornecedorAccess);
    }));
    return orcamentoitem;
}

// Item 3 (pós-QA 02/10): FEE em R$ de EXIBIÇÃO = % × base em R$ (base = soma dos R$ das linhas, pela
// mesma definição de base da regra 4), para todo orçamento estrangeiro — inclusive o legado, cujo FEE
// gravado (unitário/total na moeda) não muda. Vale para o FEE de regra percentual (Sanofi 2043 e afins:
// isAgencyFeeRuleItem), com o % gravado na linha por etapa e o gross-up do % Imposto NF da linha.
// Demais FEE (AGÊNCIA 130 Chiesi, categorias AbbVie): null — a exibição segue valorBrlLinha.
const TIPO_PERCENTUAL = (produto = {}) => String(produto?.tipoPreco ?? '').trim().toLowerCase() === 'percentual';
export function calcularFeeBrlExibicao(itensComBase = [], feeLinha = {}, cambio = 1) {
    const produto = feeLinha?.produto ?? {};
    if (!FeeIntelligence.isAgencyFeeRuleItem(produto) || !TIPO_PERCENTUAL(produto)) return null;
    const bases = FeeIntelligence.calculateRuleBaseTotals(itensComBase, feeLinha);
    const impostoNf = numero(feeLinha.imposto);
    // `base`: a base em R$ que gera o FEE exibido (rótulo da linha de FEE, round 3 item B).
    const resultado = { base: {} };
    Object.entries(MOMENTO_ETAPA).forEach(([momento, etapa]) => {
        const baseBrl = numero(bases[momento]) * cambio;
        const fee = comNfPropria(baseBrl * (numero(feeLinha[`servico${etapa}`]) / 100), impostoNf, feeLinha);
        resultado[momento] = fee;
        resultado.base[momento] = baseBrl;
    });
    // Fechamento exterior conciliado (round 3 item A): o FEE do Fechamento exibido é o recalculado pelo
    // fechamento real (fechamento_exibicao 'recalculado' + fee_fechamento_real_calculado, da tela ou da
    // API) — e a base exibida é a dele. Só exibição: o FEE gravado não muda.
    const exibicao = feeLinha.fechamento_exibicao;
    if (exibicao?.origem === 'recalculado' && exibicao.total_brl !== undefined && exibicao.total_brl !== null) {
        resultado.fechamento = numero(exibicao.total_brl);
        const meta = feeLinha.fee_fechamento_real_calculado;
        if (meta && meta.base_brl !== undefined && meta.base_brl !== null) resultado.base.fechamento = numero(meta.base_brl);
    }
    return resultado;
}

// Cópia de EXIBIÇÃO do orçamento (não muta; nunca gravar): as linhas de FEE de regra recebem
// valorBrlExibicao (calcularFeeBrlExibicao). `porLinha` mapeia a linha ORIGINAL → valores, para a grade.
// contexto: { fornecedorAccess, nacionalidade, cliente } (sem eles, do evento do orçamento).
// 1945 automática do SERVIÇOS AGÊNCIA (3027, Chiesi), 04/10: em R$ = NF 19,05% por fora, exata, sobre o R$
// das demais linhas do grupo (sem NF própria). A gravada fica na moeda do orçamento, arredondada; RSVP em R$
// num orçamento em US$ entra pelo câmbio da linha. Espelho de OrcamentoValorBrl::imposto1945ServicosAgenciaBrl.
export function imposto1945ServicosAgenciaBrl(grupo = {}, cambioGlobal = 1) {
    if (!AgenciaOrcamentoPolicy.isGrupoServicosAgencia(grupo) || !Array.isArray(grupo?.subitem)) return null;
    const linha1945 = grupo.subitem.find((linha) => String(linha?.produto?.codigo ?? '').trim() === '1945');
    if (!linha1945) return null;
    const valores = {};
    [['PreEvento', 'pre_evento'], ['Negociado', 'negociado'], ['Fechamento', 'fechamento']].forEach(([momento, chave]) => {
        const base = grupo.subitem.reduce((soma, linha) => {
            if (!linha || linha === linha1945 || String(linha?.produto?.codigo ?? '').trim() === '1945') return soma;
            const impostoNf = numero(linha.imposto);
            if (impostoNf > 0 && impostoNf < 100) return soma;
            return soma + numero(linha[`total${momento}`]) * cambioBrlLinha(linha, momento, cambioGlobal);
        }, 0);
        // D-0112: a 1945 marcada na conta direta cobra base × 19,05%.
        valores[chave] = base * fatorBaseNf(linha1945) * 0.1905 / (1 - 0.1905);
    });
    return { linha: linha1945, valores };
}

export function prepararExibicaoBrl(orcamento = {}, contexto = {}) {
    const porLinha = new Map();
    const moeda = String(orcamento?.moeda || 'real').toLowerCase();
    const cambio = numero(orcamento?.cambio);
    if (moeda === 'real' || !(cambio > 0) || !Array.isArray(orcamento?.orcamentoitem)) return { orcamento, porLinha };
    const fornecedorAccess = contexto.fornecedorAccess ?? null;
    const cliente = contexto.cliente ?? orcamento?.evento?.cliente ?? orcamento?.cliente ?? null;
    // Base: cópia rasa das linhas com as anotações da base em R$ (nenhuma linha do estado é tocada).
    const itensBase = orcamento.orcamentoitem.map((grupo) => (Array.isArray(grupo?.subitem)
        ? { ...grupo, subitem: grupo.subitem.map((linha) => ({ ...linha })) } : grupo));
    anotarBaseFeeInternacional(itensBase, {
        nacionalidade: contexto.nacionalidade ?? orcamento?.evento?.nacionalidade,
        moeda: orcamento.moeda, cambio: orcamento.cambio, baseComServico: clienteBaseFeeComServico(cliente),
        baseComImpostoItem: clienteBaseFeeComImpostoItem(cliente), baseComNfAccess: clienteBaseFeeComNfAccess(cliente),
    }, fornecedorAccess);
    anotarBaseFeeBrlExato(itensBase, { exato: true, moeda: orcamento.moeda, cambio }, fornecedorAccess);
    const orcamentoitem = orcamento.orcamentoitem.map((grupo) => {
        if (!Array.isArray(grupo?.subitem)) return grupo;
        let mudou = false;
        const imposto1945 = imposto1945ServicosAgenciaBrl(grupo, cambio);
        const subitem = grupo.subitem.map((linha) => {
            if (linha && imposto1945 && linha === imposto1945.linha) {
                mudou = true;
                return { ...linha, valorBrlExibicao: imposto1945.valores };
            }
            if (!linha || !linhaDeFee(linha)) return linha;
            const valores = calcularFeeBrlExibicao(itensBase, linha, cambio);
            if (!valores) return linha;
            porLinha.set(linha, valores);
            mudou = true;
            return { ...linha, valorBrlExibicao: valores };
        });
        return mudou ? { ...grupo, subitem } : grupo;
    });
    return { orcamento: { ...orcamento, orcamentoitem }, porLinha };
}

// Fechamento (shadow em BRL): linha com ledger concluído usa o que foi pago de fato —
// principal + IOF + IR + envio + NF sobre custos; as demais, o total da etapa convertido,
// somado aos encargos estimados quando o shadow "Atual" os considera para a linha.
export function baseFeeFechamentoInternacionalBrl({ calculado = {}, totalMomentoBrl = 0, adicionalEstimadoBrl = 0, linha = {} }) {
    if (calculado.completo) return arredondarBrl(numero(calculado.principal_brl) + numero(calculado.adicional_brl));
    return comNfAccessAutomatica(linha, totalMomentoBrl) + numero(adicionalEstimadoBrl);
}
